// Google Analytics 4 Configuration
const GA_MEASUREMENT_ID =
  process.env.VUE_APP_GA_MEASUREMENT_ID ||
  (typeof window !== "undefined" ? window.GA_MEASUREMENT_ID : null) ||
  "GA_MEASUREMENT_ID";
const GA_DEBUG = process.env.VUE_APP_GA_DEBUG === "true";

// Global gtag function (loaded from external script)
const gtag = function () {
  if (typeof window !== "undefined" && window.gtag) {
    return window.gtag.apply(this, arguments);
  } else if (typeof window !== "undefined" && window.dataLayer) {
    window.dataLayer.push(arguments);
  }
};

// Debug logging function
const debugLog = (action, data) => {
  if (GA_DEBUG) {
    console.log(`[GA4 Debug] ${action}:`, data);
  }
};

// Check if we're in a webview environment
const isWebView = () => {
  return (
    window.flutter_inappwebview ||
    navigator.userAgent.includes("wv") ||
    window.ReactNativeWebView ||
    window.webkit?.messageHandlers
  );
};

// Initialize GA4 with webview-optimized settings
// Note: disable automatic page_view to avoid duplicates; we will send page_view on route changes
gtag("config", GA_MEASUREMENT_ID, {
  // Enhanced measurement settings optimized for webview
  send_page_view: false,
  anonymize_ip: true,
  allow_google_signals: false,
  allow_ad_personalization_signals: false,
  debug_mode: GA_DEBUG,
  // Use beacon transport for better reliability in webview
  transport_type: "beacon",
});

// Set initial user properties (context)
gtag("set", "user_properties", {
  app_environment: isWebView() ? "webview" : "browser",
  bundle_type: "local_zip",
});

debugLog("GA4 Initialized", {
  measurement_id: GA_MEASUREMENT_ID,
  debug_mode: GA_DEBUG,
  is_webview: isWebView(),
  environment: isWebView() ? "webview" : "browser",
});

// GA4 Plugin for Vue
const GA4Plugin = {
  install(Vue) {
    // Network connectivity check
    const isOnline = () => {
      return navigator.onLine !== false;
    };

    // Queue for offline events
    let offlineQueue = [];

    // Process offline queue when back online
    const processOfflineQueue = () => {
      if (isOnline() && offlineQueue.length > 0) {
        debugLog("Processing offline queue", { count: offlineQueue.length });
        offlineQueue.forEach(({ args }) => {
          gtag.apply(null, args);
        });
        offlineQueue = [];
      }
    };

    // Enhanced gtag wrapper with offline support
    const safeGtag = (...args) => {
      if (isOnline()) {
        gtag.apply(null, args);
      } else {
        debugLog("Queuing offline event", args);
        offlineQueue.push({ method: "gtag", args });
      }
    };

    // Listen for online/offline events
    window.addEventListener("online", processOfflineQueue);
    window.addEventListener("offline", () => {
      debugLog("App went offline", { queued_events: offlineQueue.length });
    });

    // Add GA4 methods to Vue prototype
    Vue.prototype.$gtag = safeGtag;

    // Page view tracking method
    Vue.prototype.$trackPageView = (pagePath, pageTitle) => {
      const data = {
        page_path: pagePath,
        page_title: pageTitle,
      };
      debugLog("Page View", data);
      // Send explicit page_view event to avoid double-counting
      safeGtag("event", "page_view", data);
    };

    // Event tracking method
    Vue.prototype.$trackEvent = (eventName, eventParameters = {}) => {
      const data = {
        event_category: eventParameters.category || "engagement",
        event_label: eventParameters.label || "",
        value: eventParameters.value || 0,
        // Add webview context
        app_environment: isWebView() ? "webview" : "browser",
        ...eventParameters,
      };
      debugLog("Event", { name: eventName, parameters: data });
      safeGtag("event", eventName, data);
    };

    // Custom user properties
    Vue.prototype.$setUserProperties = (properties) => {
      // GA4 user properties API
      safeGtag("set", "user_properties", properties);
    };

    // Conversion tracking
    Vue.prototype.$trackConversion = (conversionId, conversionData = {}) => {
      safeGtag("event", "conversion", {
        send_to: conversionId,
        transaction_id: conversionData.transaction_id || "",
        value: conversionData.value || 0,
        currency: conversionData.currency || "USD",
        ...conversionData,
      });
    };

    // Purchase tracking for e-commerce
    Vue.prototype.$trackPurchase = (purchaseData) => {
      safeGtag("event", "purchase", {
        transaction_id: purchaseData.transaction_id,
        value: purchaseData.value,
        currency: purchaseData.currency || "USD",
        items: purchaseData.items || [],
        ...purchaseData,
      });
    };

    // Login tracking
    Vue.prototype.$trackLogin = (method = "unknown") => {
      safeGtag("event", "login", {
        method: method,
      });
    };

    // Sign up tracking
    Vue.prototype.$trackSignUp = (method = "unknown") => {
      safeGtag("event", "sign_up", {
        method: method,
      });
    };

    // Search tracking
    Vue.prototype.$trackSearch = (searchTerm) => {
      safeGtag("event", "search", {
        search_term: searchTerm,
      });
    };

    // Share tracking
    Vue.prototype.$trackShare = (contentType, itemId) => {
      safeGtag("event", "share", {
        content_type: contentType,
        item_id: itemId,
      });
    };

    // Game/Lottery specific events
    Vue.prototype.$trackGamePlay = (gameType, betAmount) => {
      const data = {
        content_type: "game",
        game_type: gameType,
        bet_amount: betAmount,
        currency: "LAK",
      };
      debugLog("Game Play", data);
      safeGtag("event", "game_play", data);
    };

    Vue.prototype.$trackGameWin = (gameType, winAmount) => {
      const data = {
        content_type: "game",
        game_type: gameType,
        win_amount: winAmount,
        currency: "LAK",
      };
      debugLog("Game Win", data);
      safeGtag("event", "game_win", data);
    };

    Vue.prototype.$trackDeposit = (amount, method) => {
      const data = {
        value: amount,
        currency: "LAK",
        payment_method: method,
      };
      debugLog("Deposit", data);
      safeGtag("event", "deposit", data);
    };

    Vue.prototype.$trackWithdraw = (amount, method) => {
      const data = {
        value: amount,
        currency: "LAK",
        payment_method: method,
      };
      debugLog("Withdraw", data);
      safeGtag("event", "withdraw", data);
    };
  },
};

export default GA4Plugin;
export { gtag, GA_MEASUREMENT_ID };
